import {sql} from '@vercel/postgres';
import {
  CustomerField,
  CustomersTableType,
  InvoiceForm,
  InvoicesTable,
  LatestInvoiceRaw,
  Revenue,
} from './definitions';
import {formatCurrency} from './utils';
import { createSearchService, ElasticsearchConfig } from '@org/ui';

const ELASTIC_NODE = process.env.ELASTIC_NODE ?? '';
const ELASTIC_NODE_KEY = process.env.ELASTIC_NODE_KEY ?? '';
const PRODUCT_INDEX = process.env.PRODUCT_INDEX ?? '';

if (!ELASTIC_NODE || !PRODUCT_INDEX) {
  console.warn('[lib/data] Missing Elasticsearch configuration. Check ELASTIC_NODE and PRODUCT_INDEX env variables.');
}


export async function fetchProjects(category: string, query: string) {
  try {
    const res = await fetch(`${process.env.CATALOG_API}/next-shop-api/catalog/projects/?category=${category}&${query}`,
        {next: {revalidate: 36000000}})
    const projects = await res.json()

    return projects
  } catch (error) {
    console.error('Database Error:', error);
    throw new Error('Failed to fetch revenue data.');
  }
}

export async function fetchFilterShop(section: string) {
  try {
    const normalizedSection = typeof section === 'string' ? section.trim() : '';
    if (!normalizedSection) {
      return {filters: []};
    }

    const response = await fetch(`${ELASTIC_NODE}/${PRODUCT_INDEX}/_search`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `${ELASTIC_NODE_KEY}`,
      },
      body: JSON.stringify({
        size: 500,
        _source: [
          'elastic_document.attributes',
          'elastic_document.facets',
          'sectionCode'
        ],
        query: {
          term: {
            'sectionCode.keyword': normalizedSection
          }
        }
      }),
      next: {revalidate: 36000000}
    });

    if (!response.ok) {
      throw new Error('Failed to fetch filter data');
    }

    const data = await response.json();
    const hits = Array.isArray(data?.hits?.hits) ? data.hits.hits : [];

    const serializeFacetScalar = (value: unknown): string | undefined => {
      if (value === null || value === undefined) {
        return undefined;
      }
      if (typeof value === 'string') {
        const trimmed = value.trim();
        return trimmed.length > 0 ? trimmed : undefined;
      }
      if (typeof value === 'number') {
        return Number.isFinite(value) ? String(value) : undefined;
      }
      if (typeof value === 'boolean') {
        return value ? 'true' : 'false';
      }
      if (typeof value === 'object') {
        if ('TEXT' in (value as Record<string, unknown>)) {
          return serializeFacetScalar((value as Record<string, unknown>).TEXT);
        }
        if ('VALUE' in (value as Record<string, unknown>)) {
          return serializeFacetScalar((value as Record<string, unknown>).VALUE);
        }
        return undefined;
      }
      return String(value);
    };

    const presentFacetScalar = (id: string, attribute: any): string => {
      const typeHint = typeof attribute?.type === 'string' ? attribute.type : undefined;
      if (id === 'true') {
        return 'Да';
      }
      if (id === 'false') {
        return 'Нет';
      }
      if (typeHint && typeHint.startsWith('number')) {
        const parsed = Number(id);
        return Number.isFinite(parsed) ? String(parsed) : id;
      }
      return id;
    };

    const extractFacetValues = (value: unknown, attribute: any): { id: string; label: string }[] => {
      if (value === null || value === undefined) {
        return [];
      }

      if (Array.isArray(value)) {
        const dedup = new Map<string, { id: string; label: string }>();
        value.forEach((item) => {
          extractFacetValues(item, attribute).forEach(({ id, label }) => {
            if (!dedup.has(id)) {
              dedup.set(id, { id, label });
            }
          });
        });
        return Array.from(dedup.values());
      }

      const id = serializeFacetScalar(value);
      if (!id) {
        return [];
      }

      return [{
        id,
        label: presentFacetScalar(id, attribute),
      }];
    };

    const filtersMap: Map<string, {
      CODE: string;
      NAME: string;
      values: Map<string, { ID: string; VALUE: string; PRODUCT_IDS: Set<string> }>;
    }> = new Map();

    hits.forEach((hit: any) => {
      const doc = hit?._source?.elastic_document || {};
      const attributes = doc.attributes || {};
      const facets = doc.facets || {};
      const productIdSource = hit?._source?.id ?? doc?.core?.id ?? hit?._id ?? null;
      const productId = productIdSource !== null && productIdSource !== undefined
        ? String(productIdSource)
        : undefined;

      Object.entries(attributes).forEach(([code, attribute]: [string, any]) => {
        if (!attribute || typeof attribute !== 'object') {
          return;
        }

        const isFacetable = Boolean(attribute.facetable);
        if (!isFacetable) {
          return;
        }

        const label = (attribute.display?.label || attribute.label || code) as string;
        const facetValuesRaw = facets.hasOwnProperty(code) ? facets[code] : attribute.value;
        const normalizedValues = extractFacetValues(facetValuesRaw, attribute);
        if (normalizedValues.length === 0) {
          return;
        }

        if (!filtersMap.has(code)) {
          filtersMap.set(code, {
            CODE: code,
            NAME: label,
            values: new Map(),
          });
        }

        const filterEntry = filtersMap.get(code);
        if (!filterEntry) {
          return;
        }

        normalizedValues.forEach(({ id, label: valueLabel }) => {
          if (!id || !valueLabel) {
            return;
          }
          const entry = filterEntry.values.get(id);
          if (!entry) {
            filterEntry.values.set(id, {
              ID: id,
              VALUE: valueLabel,
              PRODUCT_IDS: productId ? new Set([productId]) : new Set(),
            });
            return;
          }

          if (productId) {
            entry.PRODUCT_IDS.add(productId);
          }
        });
      });
    });

    const filters = Array.from(filtersMap.values())
      .map((entry) => {
        const valuesArray = Array.from(entry.values.values())
          .map((value) => ({
            ID: value.ID,
            VALUE: value.VALUE,
            PRODUCT_IDS: Array.from(value.PRODUCT_IDS),
          }))
          .sort((a, b) => a.VALUE.localeCompare(b.VALUE, 'ru'));
        return {
          NAME: entry.NAME,
          CODE: entry.CODE,
          VALUES: valuesArray,
        };
      })
      .filter((entry) => entry.VALUES.length > 0)
      .sort((a, b) => a.NAME.localeCompare(b.NAME, 'ru'));

    return {filters};
  } catch (error) {
    console.error('Database Error:', error);
    return {filters: []};
  }
}

export async function fetchSearchFilterShop() {
  try {
    /*    const resCategoryShop = await fetch(`${process.env.CATALOG_API}/next-shop-api/catalog/filtershop/?category=${section}`, { next: { revalidate: 1 } })
        const allCategoryShop = await resCategoryShop.json()*/
    const response = await fetch(`${ELASTIC_NODE}/${PRODUCT_INDEX}/_search`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Basic ZWxhc3RpYzp2cVVLU2ZlRktIcmcyUCE=`,
      },
      body: JSON.stringify({
        size: 0,  // Мы не запрашиваем сами документы, только агрегации
        query: {
          term: {
            "sectionCode.keyword": "ukrasheniya"  // Категория для фильтрации
          }
        },
        aggs: {
          brand_filter: {
            terms: {
              field: "BREND.keyword",
              size: 100
            }
          },
          material_filter: {
            terms: {
              field: "ATTR_METAL.keyword",
              size: 100
            }
          }
        }
      }),
      next: {revalidate: 36000000}
    });

    if (!response.ok) {
      throw new Error('Failed to fetch filter data');
    }

    const data = await response.json();

// Преобразуем ответ Elasticsearch в массив объектов с объединенными фильтрами
    const filters = [
      {
        NAME: 'Бренд',  // Название фильтра
        CODE: 'BREND',  // Код фильтра
        VALUES: data.aggregations.brand_filter.buckets.map((bucket: any) => ({
          ID: bucket.key,  // Уникальный идентификатор бренда
          VALUE: bucket.key  // Название бренда
        }))
      },
      {
        NAME: 'Марериал',  // Название фильтра
        CODE: 'ATTR_METAL',  // Код фильтра
        VALUES: data.aggregations.material_filter.buckets.map((bucket: any) => ({
          ID: bucket.key,  // Уникальный идентификатор бренда
          VALUE: bucket.key  // Название бренда
        }))
      }
    ];

    const filtersAll = {filters};  // Преобразуем в объект с ключом 'filters'
    return filtersAll;
  } catch (error) {
    console.error('Database Error:', error);
    throw new Error('Failed to fetch card data.');
  }
}


export async function fetchItemsShop(category: string, query: string) {
  try {
    // Генерируем кэш-ключ, уникальный для комбинации категории и запроса
    const cacheKey = `items_shop_${category}_${query}`;

    // Разбираем строку запроса для извлечения параметров
    const urlParams = new URLSearchParams(query);
    const sortParam = urlParams.get('sort') ?? '';

    // Фильтры, которые будем динамически добавлять
    const filters = [];

    // Добавляем фильтр по категории, только если он предоставлен
    if (category) {
      filters.push({
        "term": {
          "sectionCode.keyword": category
        }
      });
    }

    // Перебираем все параметры запроса, кроме 'page'
    // Оптимизируем запрос, чтобы избегать излишне сложных фильтров
    urlParams.forEach((value, key) => {
      if ((key === 'page') || (key === 'sort')) {
        return;
      }
      if (value) {
        // Для параметров, которые могут содержать несколько значений
        const values = value.split(',').filter(v => v.trim()).map(v => v.trim());

        if (values.length > 0) {
          const baseFacetField = `elastic_document.facets.${key}`;
          const stringValues: string[] = [];
          const numericValues: number[] = [];
          const booleanValues: boolean[] = [];

          values.forEach((candidate) => {
            if (candidate === 'true' || candidate === 'false') {
              booleanValues.push(candidate === 'true');
              return;
            }

            const numericCandidate = Number(candidate);
            if (!Number.isNaN(numericCandidate) && candidate !== '') {
              numericValues.push(numericCandidate);
              return;
            }

            stringValues.push(candidate);
          });

          const shouldClauses: any[] = [];

          if (stringValues.length > 0) {
            shouldClauses.push({
              terms: {
                [`${baseFacetField}.keyword`]: stringValues,
              },
            });
            shouldClauses.push({
              terms: {
                [`${key}.keyword`]: stringValues,
              },
            });
          }

          if (numericValues.length > 0) {
            shouldClauses.push({
              terms: {
                [baseFacetField]: numericValues,
              },
            });
            shouldClauses.push({
              terms: {
                [key]: numericValues,
              },
            });
          }

          if (booleanValues.length > 0) {
            shouldClauses.push({
              terms: {
                [baseFacetField]: booleanValues,
              },
            });
            shouldClauses.push({
              terms: {
                [key]: booleanValues,
              },
            });
          }

          if (shouldClauses.length > 0) {
            filters.push({
              bool: {
                should: shouldClauses,
                minimum_should_match: 1,
              },
            });
          }
        }
      }
    });

    // Получаем значение параметра 'page'
    const page = urlParams.get('page') ? parseInt(urlParams.get('page') as string) : 1;

    // Увеличиваем таймаут для более стабильной работы под нагрузкой
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 7000); // Увеличиваем таймаут до 7 секунд

    try {
      // Выполняем запрос к Elasticsearch с оптимизированными параметрами
      const response = await fetch(`${ELASTIC_NODE}/${PRODUCT_INDEX}/_search`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `${ELASTIC_NODE_KEY}`, // Используем константу вместо хардкода
          'Cache-Control': 'no-cache' // Отключаем HTTP кэш для получения актуальных данных
        },
        body: JSON.stringify({
          // Используем более эффективный запрос
          query: {
            "bool": {
              "must": filters.length > 0 ? filters : [{"match_all": {}}]
            }
          },
          // Сортировка по ID в порядке убывания
          sort: (() => {
            switch (sortParam) {
              case 'price_asc':
                return [
                  { 'price': { order: 'asc' } },
                  { 'id.keyword': { order: 'desc' } },
                ];
              case 'price_desc':
                return [
                  { 'price': { order: 'desc' } },
                  { 'id.keyword': { order: 'desc' } },
                ];
              case 'new':
                return [
                  { 'elastic_document.system.indexed_at': { order: 'desc' } },
                  { 'id.keyword': { order: 'desc' } },
                ];
              default:
                return [
                  { 'id.keyword': { order: 'desc' } },
                ];
            }
          })(),
          // Оптимизируем запрос, указав только нужные поля
          _source: [
            "id",
            "name",
            "code",
            "picture",
            "price",
            "priceOld",
            "sectionCode",
            "BREND",
            "ATTR_METAL",
            "labels",
            "elastic_document.labels",
            "elastic_document.commerce",
            "elastic_document.languages",
            "elastic_document.core",
            "elastic_document.core.title",
            "elastic_document.media",
            "image_gallery"
          ],
          // Параметры пагинации
          from: (page - 1) * 24,
          size: 24,
          track_total_hits: true
        }),
        signal: controller.signal,
        next: {
          revalidate: 3600, // 1 час кэширования
          tags: ['items_shop', cacheKey],
        },
      });

      // Очищаем таймаут после завершения запроса
      clearTimeout(timeoutId);

      // Проверка статуса ответа
      if (!response.ok) {
        console.error(`Ошибка Elasticsearch: ${response.status} ${response.statusText}`);
        return {
          item: [],
          totalItems: 0,
          error: `Ошибка сервера: ${response.status}`
        };
      }

      const searchResult = await response.json();

      // Проверка наличия ошибок в ответе Elasticsearch
      if (searchResult.error) {
        console.error('Ошибка Elasticsearch:', searchResult.error);
        return {
          item: [],
          totalItems: 0,
          error: 'Ошибка поиска товаров'
        };
      }

      // Получаем список товаров
      const itemsShop = searchResult.hits.hits.map((hit: any) => hit._source);

      // Получаем общее количество элементов
      const totalItems = searchResult.hits.total.value;

      // Формируем результат
      return {
        item: itemsShop,
        totalItems: totalItems
      };

    } catch (error: any) {
      if (error.name === 'AbortError') {
        console.error('Запрос к Elasticsearch был прерван из-за таймаута');
        // Возвращаем пустой объект с элементами и флаг ошибки
        return {
          item: [],
          totalItems: 0,
          error: 'Превышено время ожидания'
        };
      }

      // Логируем другие ошибки с дополнительной информацией
      console.error('Elasticsearch error:', error.message || error);

      // Возвращаем результат с информацией об ошибке
      return {
        item: [],
        totalItems: 0,
        error: error.message || 'Неизвестная ошибка'
      };
    } finally {
      clearTimeout(timeoutId);
    }
  } catch (error) {
    console.error('Database Error:', error);
    // Возвращаем пустой объект с флагом ошибки
    return {
      item: [],
      totalItems: 0,
      error: error instanceof Error ? error.message : 'Неизвестная ошибка'
    };
  }
}

type RecommendationParams = {
  sectionCodes?: string | string[] | null;
  brand?: string | null;
  excludeCode?: string | null;
  limit?: number;
};

export async function fetchRecommendedProducts({
  sectionCodes,
  brand,
  excludeCode,
  limit = 8,
}: RecommendationParams) {
  try {
    const indexCandidates = [PRODUCT_INDEX];

    const normalizedSections = Array.isArray(sectionCodes)
      ? sectionCodes.filter(Boolean)
      : sectionCodes
        ? String(sectionCodes)
            .split(',')
            .map((code) => code.trim())
            .filter(Boolean)
        : [];

    const leafSection = normalizedSections.length > 0
      ? normalizedSections[normalizedSections.length - 1]
      : undefined;

    const attempts: Array<{
      section?: string;
      brand?: string;
    }> = [];

    if (leafSection && brand) {
      attempts.push({ section: leafSection, brand });
    }
    if (leafSection) {
      attempts.push({ section: leafSection });
    }
    if (brand) {
      attempts.push({ brand });
    }
    attempts.push({});

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);

    try {
      for (const attempt of attempts) {
        for (const indexName of indexCandidates) {
          const mustClauses: any[] = [];
          const shouldClauses: any[] = [];
          const mustNotClauses: any[] = [];

          if (attempt.section) {
            mustClauses.push({
              term: {
                'sectionCode.keyword': attempt.section,
              },
            });
          } else if (normalizedSections.length > 0) {
            mustClauses.push({
              terms: {
                'sectionCode.keyword': normalizedSections,
              },
            });
          }

          if (attempt.brand) {
            mustClauses.push({
              term: {
                'BREND.keyword': attempt.brand,
              },
            });
          } else if (brand) {
            shouldClauses.push({
              term: {
                'BREND.keyword': brand,
              },
            });
          }

          if (excludeCode) {
            mustNotClauses.push({
              term: {
                'code.keyword': excludeCode,
              },
            });
          }

          const response = await fetch(`${ELASTIC_NODE}/${indexName}/_search`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `${ELASTIC_NODE_KEY}`,
            },
            body: JSON.stringify({
              size: Math.max(0, Math.min(limit, 24)),
              query: mustClauses.length === 0 && shouldClauses.length === 0 && mustNotClauses.length === 0
                ? { match_all: {} }
                : {
                    bool: {
                      ...(mustClauses.length > 0 ? { must: mustClauses } : {}),
                      ...(shouldClauses.length > 0 ? { should: shouldClauses } : {}),
                      ...(mustNotClauses.length > 0 ? { must_not: mustNotClauses } : {}),
                    },
                  },
              sort: [
                { '_score': { order: 'desc' } },
                { 'id.keyword': { order: 'desc' } },
              ],
              _source: [
              'id',
              'name',
              'code',
              'picture',
              'price',
              'priceOld',
              'sectionCode',
              'BREND',
              'labels',
              'elastic_document.labels',
              'image_gallery',
              'available',
            ],
            track_total_hits: false,
          }),
          signal: controller.signal,
          next: {
            revalidate: 60,
            tags: ['recommendations'],
          },
        });

          if (response.status === 404) {
            continue;
          }

          const data = await response.json();
          console.log('[fetchRecommendedProducts] attempt:', attempt, 'index:', indexName, 'status:', response.status, 'items:', Array.isArray(data?.hits?.hits) ? data.hits.hits.length : 'n/a');

          if (!response.ok) {
            console.error('Failed to fetch recommended products:', response.status, response.statusText, 'index:', indexName, 'body:', data);
            return [];
          }

          const hits = Array.isArray(data?.hits?.hits) ? data.hits.hits : [];
          if (hits.length > 0) {
            return hits.map((hit: any) => hit?._source).filter(Boolean);
          }
        }

        // Если ответ успешный, но пустой, пробуем следующий индекс
      }

      return [];
    } catch (error: any) {
      if (error?.name === 'AbortError') {
        console.error('Recommendations request aborted due to timeout');
        return [];
      }
      console.error('Recommendations fetch error:', error);
      return [];
    } finally {
      clearTimeout(timeoutId);
    }
  } catch (error) {
    console.error('Database Error:', error);
    return [];
  }
}

// Конфигурация Elasticsearch
const elasticsearchConfig: ElasticsearchConfig = {
  node: ELASTIC_NODE,
  index: PRODUCT_INDEX,
  auth: ELASTIC_NODE_KEY
};

// Создаем экземпляр сервиса поиска
const searchService = createSearchService({
  config: elasticsearchConfig,
  pageSize: 24,
  timeout: 7000,
  dataStructure: 'elastic_document',
  searchFields: [
    "name^3",
    "code^2",
    "elastic_document.core.title^3",
    "elastic_document.core.sku^2",
    "elastic_document.seo.meta_description^2",
    "elastic_document.taxonomy.category^2",
    "elastic_document.core.slug"
  ],
  sortFields: {
    price_asc: 'elastic_document.facets.price',
    price_desc: 'elastic_document.facets.price',
    new: 'elastic_document.system.indexed_at',
    relevance: '_score'
  }
});

export async function fetchSearchItemsShop(category: string, query: string) {
  try {
    // Получаем текущий язык из headers
    let langCode: string | undefined = undefined;
    try {
      const { getCurrentLanguageCode } = await import('@/lib/server/get-current-language');
      langCode = await getCurrentLanguageCode();
    } catch (e) {
      // Если не удалось получить язык, продолжаем без него
      console.warn('Could not determine language code:', e);
    }

    const result = await searchService.searchItems(category, query, langCode);
    return result;
  } catch (error) {
    console.error('Search Error:', error);
    return {
      item: [],
      totalItems: 0,
      error: error instanceof Error ? error.message : 'Ошибка поиска'
    };
  }
}


export async function fetchHelp(userId: string) {
  try {
    const res = await fetch(`${process.env.API_URL_CONTENT}services/help.php?userId=${userId}`, {next: {revalidate: Number(process.env.REVALIDATE)}})
    const helpItems = await res.json()

    return helpItems
  } catch (error) {
    console.error('Database Error:', error);
    //throw new Error('Failed to fetch revenue data.');
  }
}

export async function fetchCategory() {
  try {
    // Artificially delay a response for demo purposes.
    // Don't do this in production :)

    // console.log('Fetching revenue data...');
    // await new Promise((resolve) => setTimeout(resolve, 3000));
//, { next: { revalidate: 360000 } })
    // , {cache: 'force-cache'}
    const resCategory = await fetch(`${process.env.API_URL_CONTENT}services/category.php`, {next: {revalidate: Number(process.env.REVALIDATE)}})
    const allCategory = await resCategory.json()

    return allCategory
  } catch (error) {
    console.error('Database Error:', error);
    throw new Error('Failed to fetch card data.');
  }
}

export async function fetchItemShop(itemCode: string) {
  try {
    // Фильтры, которые будем динамически добавлять
    const filters = [];

    // Добавляем фильтр по категории
    if (itemCode) {
      filters.push({
        "term": {
          "code.keyword": itemCode
        }
      });
    }

    const cacheTags = ['item_shop'];
    if (itemCode) {
      cacheTags.push(`item_shop_${itemCode}`);
    }

    // Используем AbortController для установки таймаута запроса
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 секунд таймаут

    try {
      // Выполняем запрос к Elasticsearch
      const response = await fetch(`${ELASTIC_NODE}/${PRODUCT_INDEX}/_search`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Basic ZWxhc3RpYzp2cVVLU2ZlRktIcmcyUCE=`,
        },
        body: JSON.stringify({
          query: {
            "bool": {
              "must": filters
            }
          },
          _source: [
            "id",
            "name",
            "code",
            "picture",
            "price",
            "priceOld",
            "sectionCode",
            "BREND",
            "ATTR_METAL",
            "labels",
            "description",
            "elastic_document",
            "elastic_document.languages",
            "elastic_document.core",
            "elastic_document.core.title",
            "elastic_document.taxonomy"
          ]
        }),
        signal: controller.signal,
        next: {
          revalidate: 3600,
          tags: cacheTags,
        },
      });

      // Очищаем таймаут после завершения запроса
      clearTimeout(timeoutId);

      const searchResult = await response.json();

      // Получаем список товаров
      const itemsShop = searchResult.hits.hits.map((hit: any) => hit._source);

      // Получаем общее количество элементов, удовлетворяющих условиям поиска
      const totalItems = searchResult.hits.total.value;

      let itemsShopProducts: { item?: any; totalItems?: number; filters?: any } = {};

      itemsShopProducts.item = itemsShop;
      itemsShopProducts.totalItems = totalItems;

      // Проверяем наличие товара
      if (itemsShopProducts.item && itemsShopProducts.item.length > 0) {
        let itemShop = itemsShopProducts.item[0];
        return itemShop;
      } else {
        return { error: 'Товар не найден' };
      }

    } catch (error: any) {
      if (error.name === 'AbortError') {
        console.error('Запрос к Elasticsearch был прерван из-за таймаута');
        return { error: 'Таймаут запроса товара' };
      }
      throw error; // Пробрасываем другие ошибки
    } finally {
      clearTimeout(timeoutId);
    }
  } catch (error) {
    console.error('Database Error:', error);
    return { error: 'Ошибка при получении данных товара' };
  }
}


export async function fetchCategoryShop(section?: string) {
  try {
    const isSectionProvided = typeof section === 'string' && section.trim() !== '';
    //console.log(isSectionProvided);
    // Определяем запрос в зависимости от наличия параметра section
    const query = isSectionProvided
        ? {term: {'parentCode.keyword': section}}
        : {term: {depthLevel: 1}};
    //console.log(query)

    // Используем AbortController для установки таймаута запроса
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 секунд таймаут

    try {
      const response = await fetch(`${ELASTIC_NODE}/${process.env.CATEGORIES}/_search`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Basic ZWxhc3RpYzp2cVVLU2ZlRktIcmcyUCE=`,
        },
        body: JSON.stringify({
          query: query,
          // Уменьшаем размер запроса для более быстрого ответа
          size: 100, // Было: 1000
        }),
        signal: controller.signal,
        next: {
          revalidate: 3600, // Уменьшаем время кэширования до 1 часа
          tags: ['category_shop'],
        },
      });

      // Очищаем таймаут после завершения запроса
      clearTimeout(timeoutId);

      const searchResult = await response.json();
      const allCategoryShop = searchResult.hits.hits.map((hit: any) => hit._source);
      //console.log(allCategoryShop)
      return allCategoryShop;

    } catch (error: any) {
      if (error.name === 'AbortError') {
        console.error('Запрос к Elasticsearch был прерван из-за таймаута');
        return []; // Возвращаем пустой массив вместо ошибки
      }
      throw error; // Пробрасываем другие ошибки
    } finally {
      clearTimeout(timeoutId); // На всякий случай очищаем таймаут здесь тоже
    }
  } catch (error) {
    console.error('Database Error:', error);
    // Возвращаем пустой массив вместо ошибки, чтобы страница не крашилась
    return [];
  }
}


export async function fetchCategoryInfo(section: string) {
  try {
    const isSectionProvided = typeof section === 'string' && section.trim() !== '';

    // Определяем запрос в зависимости от наличия параметра section
    const query = isSectionProvided
        ? {term: {"code.keyword": section}}
        : {term: {"code.keyword": section}};

    // Используем AbortController для установки таймаута запроса
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 секунд таймаут

    try {
      const response = await fetch(`${ELASTIC_NODE}/${process.env.CATEGORIES}/_search`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Basic ZWxhc3RpYzp2cVVLU2ZlRktIcmcyUCE=`,
        },
        body: JSON.stringify({
          query: query,
        }),
        signal: controller.signal,
        next: {
          revalidate: 36000000, // Уменьшаем время кэширования до 1 часа
          tags: ['category_info'],
        },
      });

      // Очищаем таймаут после завершения запроса
      clearTimeout(timeoutId);

      const searchResult = await response.json();
      //console.log(searchResult)
      const categoryInfo = searchResult.hits.hits.map((hit: any) => hit._source);

      return categoryInfo[0] || { error: 'Категория не найдена' };

    } catch (error: any) {
      if (error.name === 'AbortError') {
        console.error('Запрос к Elasticsearch был прерван из-за таймаута');
        return { error: 'Таймаут запроса категории' };
      }
      throw error; // Пробрасываем другие ошибки
    } finally {
      clearTimeout(timeoutId);
    }
  } catch (error) {
    console.error('Database Error:', error);
    return { error: 'Ошибка при получении данных категории' };
  }
}

export async function fetchTopCategories() {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);

    try {
      const response = await fetch(`${ELASTIC_NODE}/${process.env.CATEGORIES}/_search`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Basic ZWxhc3RpYzp2cVVLU2ZlRktIcmcyUCE=`,
        },
        body: JSON.stringify({
          query: {
            bool: {
              filter: [
                { term: { isTop: true } },
                { term: { depthLevel: 1 } }
              ]
            }
          },
          size: 10,
          track_total_hits: false
        }),
        signal: controller.signal,
        next: { revalidate: 600, tags: ['top_categories'] },
      });

      clearTimeout(timeoutId);

      const searchResult = await response.json();
      /*console.log(searchResult)*/
      const categories = searchResult.hits.hits.map((hit: any) => hit._source);
      return categories;
    } catch (error: any) {
      if (error.name === 'AbortError') {
        console.error('Запрос к Elasticsearch был прерван из-за таймаута');
        return [];
      }
      throw error;
    } finally {
      clearTimeout(timeoutId);
    }
  } catch (error) {
    console.error('Database Error:', error);
    return [];
  }
}

export async function fetchTopItems(paramName: string, paramValue: string) {
  try {
    // Проверяем, что параметры предоставлены
    const isParamNameProvided = typeof paramName === 'string' && paramName.trim() !== '';
    const isParamValueProvided = typeof paramValue === 'string' && paramValue.trim() !== '';
    
    if (!isParamNameProvided || !isParamValueProvided) {
      throw new Error('Параметры paramName и paramValue обязательны');
    }
    
    // Используем AbortController для установки таймаута запроса
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 секунд таймаут
    
    try {
      // Выполняем запрос к Elasticsearch для поиска товаров с указанным параметром
      const response = await fetch(`${ELASTIC_NODE}/${PRODUCT_INDEX}/_search`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `${ELASTIC_NODE_KEY}`,
        },
        body: JSON.stringify({
          query: {
            term: {
              [`${paramName}.keyword`]: paramValue
            }
          },
          size: 10, // Ограничиваем результат 10 элементами
          sort: [
            { "id.keyword": { "order": "desc" } } // Сортировка по ID для стабильности результатов
          ]
        }),
        signal: controller.signal,
        next: {
          revalidate: 3600,
          tags: ['top_items'],
        }, // Кэширование на 1 час

      });
      
      // Очищаем таймаут после завершения запроса
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new Error(`Ошибка при запросе: ${response.status}`);
      }
      
      const searchResult = await response.json();
      const items = Array.isArray(searchResult?.hits?.hits)
        ? searchResult.hits.hits.map((hit: any) => hit._source)
        : [];

      console.log('[fetchTopItems] index:', PRODUCT_INDEX, 'param:', paramName, 'value:', paramValue, 'items:', items.length);

      return items;
    } catch (error: any) {
      if (error.name === 'AbortError') {
        console.error('Запрос к Elasticsearch был прерван из-за таймаута');
        return []; // Возвращаем пустой массив вместо ошибки
      }
      throw error; // Пробрасываем другие ошибки
    } finally {
      clearTimeout(timeoutId);
    }
  } catch (error) {
    console.error('Database Error:', error);
    return []; // Возвращаем пустой массив в случае ошибки
  }
}

// Популярные товары (топ-10) по убыванию популярности/просмотров/рейтинга с фолбэком
export async function fetchPopularItems(limit: number = 10) {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);

    try {
      const response = await fetch(`${ELASTIC_NODE}/${PRODUCT_INDEX}/_search`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `${ELASTIC_NODE_KEY}`,
        },
        body: JSON.stringify({
          size: Math.max(1, Math.min(limit, 24)),
          query: { match_all: {} },
          sort: [
            { 'popularity': { order: 'desc', unmapped_type: 'long', missing: '_last' } },
            { 'views': { order: 'desc', unmapped_type: 'long', missing: '_last' } },
            { 'rating': { order: 'desc', unmapped_type: 'double', missing: '_last' } },
            { 'sales': { order: 'desc', unmapped_type: 'long', missing: '_last' } },
            { 'id.keyword': { order: 'desc' } },
          ],
          _source: [
            'id',
            'name',
            'code',
            'picture',
            'price',
            'priceOld',
            'sectionCode',
            'BREND',
            'labels',
            'elastic_document.labels',
            'image_gallery',
            'available',
          ],
          track_total_hits: false,
        }),
        signal: controller.signal,
        next: {
          revalidate: 3600,
          tags: ['popular_items'],
        },
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        console.error('[fetchPopularItems] failed:', response.status, response.statusText);
        return [];
      }

      const data = await response.json();
      const hits = Array.isArray(data?.hits?.hits) ? data.hits.hits : [];
      return hits.map((hit: any) => hit?._source).filter(Boolean);
    } catch (error: any) {
      if (error?.name === 'AbortError') {
        console.error('[fetchPopularItems] aborted due to timeout');
        return [];
      }
      console.error('[fetchPopularItems] error:', error);
      return [];
    }
  } catch (error) {
    console.error('Database Error:', error);
    return [];
  }
}

export async function fetchTopSales(section: string) {
  try {
    const isSectionProvided = typeof section === 'string' && section.trim() !== '';

    // Определяем запрос в зависимости от наличия параметра section
    const query = isSectionProvided
        ? {term: {"code.keyword": section}}
        : {term: {"code.keyword": section}};
    const response = await fetch(`${ELASTIC_NODE}/${process.env.CATEGORIES}/_search`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Basic ZWxhc3RpYzp2cVVLU2ZlRktIcmcyUCE=`,
      },
      body: JSON.stringify({
        query: query,
      }),
      next: {revalidate: 36000000}
    });


    const searchResult = await response.json();
    const categoryInfo = searchResult.hits.hits.map((hit: any) => hit._source);

    return categoryInfo[0];

  } catch (error) {
    console.error('Database Error:', error);
    throw new Error('Failed to fetch category data.');
  }

}

export async function fetchRevenue() {
  try {
    // Artificially delay a response for demo purposes.
    // Don't do this in production :)

    // console.log('Fetching revenue data...');
    // await new Promise((resolve) => setTimeout(resolve, 3000));

    const data = await sql<Revenue>`SELECT *
                                    FROM revenue`;

    // console.log('Data fetch completed after 3 seconds.');

    return data.rows;
  } catch (error) {
    console.error('Database Error:', error);
    throw new Error('Failed to fetch revenue data.');
  }
}

export async function fetchLatestInvoices() {
  try {
    const data = await sql<LatestInvoiceRaw>`
      SELECT invoices.amount, customers.name, customers.image_url, customers.email, invoices.id
      FROM invoices
             JOIN customers ON invoices.customer_id = customers.id
      ORDER BY invoices.date DESC LIMIT 5`;

    const latestInvoices = data.rows.map((invoice) => ({
      ...invoice,
      amount: formatCurrency(invoice.amount),
    }));
    return latestInvoices;
  } catch (error) {
    console.error('Database Error:', error);
    throw new Error('Failed to fetch the latest invoices.');
  }
}

export async function fetchCardData() {
  try {
    // You can probably combine these into a single SQL query
    // However, we are intentionally splitting them to demonstrate
    // how to initialize multiple queries in parallel with JS.
    const invoiceCountPromise = sql`SELECT COUNT(*)
                                    FROM invoices`;
    const customerCountPromise = sql`SELECT COUNT(*)
                                     FROM customers`;
    const invoiceStatusPromise = sql`SELECT SUM(CASE WHEN status = 'paid' THEN amount ELSE 0 END)    AS "paid",
                                            SUM(CASE WHEN status = 'pending' THEN amount ELSE 0 END) AS "pending"
                                     FROM invoices`;

    const data = await Promise.all([
      invoiceCountPromise,
      customerCountPromise,
      invoiceStatusPromise,
    ]);

    const numberOfInvoices = Number(data[0].rows[0].count ?? '0');
    const numberOfCustomers = Number(data[1].rows[0].count ?? '0');
    const totalPaidInvoices = formatCurrency(data[2].rows[0].paid ?? '0');
    const totalPendingInvoices = formatCurrency(data[2].rows[0].pending ?? '0');

    return {
      numberOfCustomers,
      numberOfInvoices,
      totalPaidInvoices,
      totalPendingInvoices,
    };
  } catch (error) {
    console.error('Database Error:', error);
    throw new Error('Failed to fetch card data.');
  }
}

const ITEMS_PER_PAGE = 6;

export async function fetchFilteredInvoices(
    query: string,
    currentPage: number,
) {
  const offset = (currentPage - 1) * ITEMS_PER_PAGE;

  try {
    const invoices = await sql<InvoicesTable>`
      SELECT invoices.id,
             invoices.amount,
             invoices.date,
             invoices.status,
             customers.name,
             customers.email,
             customers.image_url
      FROM invoices
             JOIN customers ON invoices.customer_id = customers.id
      WHERE customers.name ILIKE ${`%${query}%`}
         OR
        customers.email ILIKE ${`%${query}%`}
         OR
        invoices.amount::text ILIKE ${`%${query}%`}
         OR
        invoices.date::text ILIKE ${`%${query}%`}
         OR
        invoices.status ILIKE ${`%${query}%`}
      ORDER BY invoices.date DESC
        LIMIT ${ITEMS_PER_PAGE}
      OFFSET ${offset}
    `;

    return invoices.rows;
  } catch (error) {
    console.error('Database Error:', error);
    throw new Error('Failed to fetch invoices.');
  }
}

export async function fetchItemsPages(query: string) {
  try {

    //const totalPages = Math.ceil(Number(count.rows[0].count) / ITEMS_PER_PAGE);
    return 10;
  } catch (error) {
    console.error('Database Error:', error);
    throw new Error('Failed to fetch total number of invoices.');
  }
}

export async function fetchInvoiceById(id: string) {
  try {
    const data = await sql<InvoiceForm>`
      SELECT invoices.id,
             invoices.customer_id,
             invoices.amount,
             invoices.status
      FROM invoices
      WHERE invoices.id = ${id};
    `;

    const invoice = data.rows.map((invoice) => ({
      ...invoice,
      // Convert amount from cents to dollars
      amount: invoice.amount / 100,
    }));

    return invoice[0];
  } catch (error) {
    console.error('Database Error:', error);
    throw new Error('Failed to fetch invoice.');
  }
}

export async function fetchCustomers() {
  try {
    const data = await sql<CustomerField>`
      SELECT id,
             name
      FROM customers
      ORDER BY name ASC
    `;

    const customers = data.rows;
    return customers;
  } catch (err) {
    console.error('Database Error:', err);
    throw new Error('Failed to fetch all customers.');
  }
}

export async function fetchFilteredCustomers(query: string) {
  try {
    const data = await sql<CustomersTableType>`
      SELECT customers.id,
             customers.name,
             customers.email,
             customers.image_url,
             COUNT(invoices.id)                                                         AS total_invoices,
             SUM(CASE WHEN invoices.status = 'pending' THEN invoices.amount ELSE 0 END) AS total_pending,
             SUM(CASE WHEN invoices.status = 'paid' THEN invoices.amount ELSE 0 END)    AS total_paid
      FROM customers
             LEFT JOIN invoices ON customers.id = invoices.customer_id
      WHERE customers.name ILIKE ${`%${query}%`}
         OR
        customers.email ILIKE ${`%${query}%`}
      GROUP BY customers.id, customers.name, customers.email, customers.image_url
      ORDER BY customers.name ASC
    `;

    const customers = data.rows.map((customer) => ({
      ...customer,
      total_pending: formatCurrency(customer.total_pending),
      total_paid: formatCurrency(customer.total_paid),
    }));

    return customers;
  } catch (err) {
    console.error('Database Error:', err);
    throw new Error('Failed to fetch customer table.');
  }
}

export async function fetchFaq(sectionCode: string) {
  try {
    const res = await fetch(`${process.env.API_URL_CONTENT}services/faq/index.php`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ sectionCode }),
      next: {
        revalidate: 36000000,
        tags: ['fetchServicesProect'],
      }
    })
    const faqs = await res.json()

    return faqs
  } catch (error) {
    console.error('Database Error:', error);
    throw new Error('Failed to fetch revenue data.');
  }
}


export async function fetchShops(sectionCode: string) {
  try {
    const res = await fetch(`${process.env.API_URL_CONTENT}services/shops/index.php`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ sectionCode }),
      next: {
        revalidate: 3600000,
        tags: ['fetchServicesProect'],
      }
    })
    const shops = await res.json()

    return shops
  } catch (error) {
    console.error('Database Error:', error);
    throw new Error('Failed to fetch revenue data.');
  }
}

export async function fetchMenu() {
  try {
    const res = await fetch(`${process.env.API_URL_CONTENT}services/menu/index.php`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({}),
      next: {
        revalidate: 3600, // Кэшируем на 1 час
        tags: ['menu'],
      }
    })
    const menu = await res.json()

    return menu
  } catch (error) {
    console.error('Database Error:', error);
    throw new Error('Failed to fetch menu data.');
  }
}

// Кеш для меню на клиентской стороне
let menuCache: any = null
let menuCacheTimestamp: number = 0
const CACHE_DURATION = 3600 * 1000 // 1 час в миллисекундах

// Функция для очистки кеша меню
export function clearMenuCache() {
  menuCache = null
  menuCacheTimestamp = 0
}

// Клиентская версия для использования в компонентах
export async function fetchMenuClient() {
  try {
    // Проверяем кеш
    const now = Date.now()
    if (menuCache && (now - menuCacheTimestamp) < CACHE_DURATION) {
      return menuCache
    }

    // Сначала пробуем локальный API-роут (если доступен)
    let res = await fetch(`/api/menu`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    })
    if (!res.ok) {
      const proxyErrorText = await res.text().catch(() => '')
      console.warn('Прокси недоступен, пробуем прямой запрос:', proxyErrorText)
      // Фоллбек на прямой запрос к бэкенду (CORS включён на сервере)
      res = await fetch(`${process.env.API_URL_CONTENT}services/menu/index.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      })
      if (!res.ok) {
        const errorText = await res.text().catch(() => '')
        throw new Error(`HTTP error! status: ${res.status} - ${errorText}`)
      }
    }
    
    const menu = await res.json()
    
    // Сохраняем в кеш
    menuCache = menu
    menuCacheTimestamp = now
    
    return menu
  } catch (error) {
    console.error('Menu fetch error:', error);
    throw new Error(`Failed to fetch menu data: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}
